import {
  MutationCache,
  onlineManager,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { keepPreviousFilteredData } from '@/api/keepPreviousFilteredData';
import { PageLoader } from '@/components/common/PageLoader';
import { RouteErrorState } from '@/components/common/RouteErrorState';
import { RouteNotFound } from '@/components/common/RouteNotFound';
import { refreshPermissionsAfterDenial } from '@/features/auth/utils/refreshPermissionsAfterDenial';

onlineManager.setOnline(navigator.onLine);

function onAccessDenied(error: unknown) {
  void refreshPermissionsAfterDenial(error, queryClient);
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAccessDenied }),
  mutationCache: new MutationCache({ onError: onAccessDenied }),
  defaultOptions: {
    queries: {
      networkMode: 'offlineFirst',
      retry: (failureCount) => onlineManager.isOnline() && failureCount < 1,
      refetchOnWindowFocus: true,
      placeholderData: keepPreviousFilteredData,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPendingComponent: PageLoader,
  defaultPendingMinMs: 200,
  defaultErrorComponent: RouteErrorState,
  defaultNotFoundComponent: RouteNotFound,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
